import { useEffect, useRef, useState } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { STATUS_GROUPS, STATUS_TONES, statusTone } from '../data/patientStatuses.js'

// What a patient with NO booking left can be set to. มีนัดแล้ว is left out — api
// 166 turns it into Error when there is no qualifying booking — and so is Error,
// which is the absence of an answer, not one.
const GROUPS = STATUS_GROUPS.filter((g) => g.label !== 'นัดหมาย' && g.label !== 'ระบบ')

/**
 * Patient-status picker for the appointment drawer's สถานะคนไข้ panel.
 *
 * PICKS, NEVER WRITES. StatusControl saves on click; this must not, because the
 * choice only means something once the appointment edit beside it is committed —
 * the drawer writes it after the appointment (ApptEditDrawer#finishSave).
 *
 * Same look as ApptStatusSelect, its neighbour on the left, so the pair reads as
 * one row. `invalid` rings it red while a choice is still owed.
 */
export default function PatientStatusSelect({ value, onChange, invalid, disabled, className = '', placeholder = '— เลือกสถานะคนไข้ —' }) {
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    function onDoc(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [])

  const v = String(value ?? '').trim()
  const tone = STATUS_TONES[statusTone(v)] || STATUS_TONES.none

  return (
    <div className="relative" ref={ref}>
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)}
        className={`${className} flex items-center gap-2 text-left ${invalid ? '!border-rose-300 !ring-4 !ring-rose-500/10' : ''} ${disabled ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}>
        <span className={`w-2 h-2 rounded-full flex-none ring-1 ring-black/5 ${v ? tone.dot : 'bg-slate-300'}`} />
        <span className={`truncate ${v ? '' : 'text-slate-400'}`}>{v || placeholder}</span>
        <ChevronDown size={15} className="ml-auto flex-none text-slate-400" />
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full min-w-[240px] max-h-72 overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-900/10 p-1">
          {GROUPS.map((g) => (
            <div key={g.label}>
              <div className="px-2.5 pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{g.label}</div>
              {g.items.map((s) => {
                const t = STATUS_TONES[statusTone(s)] || STATUS_TONES.none
                const sel = s === v
                return (
                  <button key={s} type="button" onClick={() => { onChange?.(s); setOpen(false) }}
                    className={`flex w-full items-center gap-2.5 text-left px-2.5 py-2 rounded-lg text-[14px] transition-colors hover:bg-slate-50 ${sel ? 'bg-slate-50 font-medium' : ''}`}>
                    <span className={`w-2 h-2 rounded-full flex-none ring-1 ring-black/5 ${t.dot}`} />
                    <span className="flex-1 truncate text-slate-700">{s}</span>
                    {sel && <Check size={14} strokeWidth={3} className="flex-none text-brand-600" />}
                  </button>
                )
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
